import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaTimes, FaSave, FaBuilding } from 'react-icons/fa';
import LedgerModal from '../../components/common/LedgerModal';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Companies.css';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    contact: {
      email: '',
      phone: '',
      website: ''
    },
    assignedTo: '',
    status: 'active'
  });

  useEffect(() => {
    loadCompanies();
    loadUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/companies');
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await api.delete(`/api/companies/${id}`);
        setCompanies(companies.filter(c => c._id !== id));
      } catch (err) {
        console.error('Error deleting company:', err);
        setError('Failed to delete company');
      }
    }
  };

  const openAddModal = () => {
    setEditingCompany(null);
    setFormData({
      name: '',
      description: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      contact: {
        email: '',
        phone: '',
        website: ''
      },
      assignedTo: '',
      status: 'active'
    });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      description: company.description || '',
      address: {
        street: company.address?.street || '',
        city: company.address?.city || '',
        state: company.address?.state || '',
        zipCode: company.address?.zipCode || '',
        country: company.address?.country || ''
      },
      contact: {
        email: company.contact?.email || '',
        phone: company.contact?.phone || '',
        website: company.contact?.website || ''
      },
      assignedTo: company.assignedTo?._id || '',
      status: company.status || 'active'
    });
    setShowModal(true);
    setError('');
  };

  const openViewModal = (company) => {
    setViewingCompany(company);
  };

  const openLedgerForCompany = () => {
    setShowLedgerModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setViewingCompany(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.name || !formData.contact.email) {
        throw new Error('Please fill in Name and Email');
      }

      if (editingCompany) {
        const response = await api.put(`/api/companies/${editingCompany._id}`, formData);
        setCompanies(companies.map(c => c._id === editingCompany._id ? response.data.company : c));
      } else {
        const response = await api.post('/api/companies', formData);
        setCompanies([...companies, response.data.company]);
      }

      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save company');
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    ((c.contact?.email || c.contactEmail) && (c.contact?.email || c.contactEmail).toLowerCase().includes(search.toLowerCase())) ||
    (c.contactPerson && c.contactPerson.toLowerCase().includes(search.toLowerCase())) ||
    (c.assignedTo?.name && c.assignedTo.name.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginatedCompanies = filteredCompanies.slice(pageStart, pageEnd);

  if (loading && companies.length === 0) return <Loading />;

  return (
    <div className="companies-container">
      <div className="companies-header">
        <div>
          <h1 className="companies-title">Company Management</h1>
          <p className="companies-subtitle">Create, edit, and manage companies</p>
        </div>
        <button className="add-company-btn" onClick={openAddModal}>
          <FaPlus /> Add New Company
        </button>
      </div>

      <input
        type="text"
        placeholder="Search companies by name, email, or assigned user..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="search-input"
      />

      <div className="companies-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filteredCompanies.length === 0 ? 0 : (pageStart + 1)}-
            {Math.min(filteredCompanies.length, pageEnd)} of {filteredCompanies.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>
        <table className="companies-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Contact Person</th>
              <th>Email</th>
              <th>Phone</th>

              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>No companies found.</td>
              </tr>
            ) : (
              paginatedCompanies.map(company => (
                <tr key={company._id}>
                  <td>
                    <div className="company-name-cell">
                      <FaBuilding className="company-icon" />
                      <span>{company.name}</span>
                    </div>
                  </td>
                  <td>{company.contactPerson || company.assignedTo?.name || 'N/A'}</td>
                  <td>{company.contact?.email || company.contactEmail || 'N/A'}</td>
                  <td>{company.contact?.phone || 'N/A'}</td>

                  <td>
                    <span className={`status-badge ${company.status}`}>
                      {company.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-row">
                      <button
                        onClick={() => openViewModal(company)}
                        className="action-btn view"
                        title="View Company"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => openEditModal(company)}
                        className="action-btn edit"
                        title="Edit Company"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(company._id)}
                        className="action-btn delete"
                        title="Delete Company"
                      >
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCompany ? 'Edit Company' : 'Add New Company'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="company-form">
              <div className="form-group">
                <label htmlFor="name">Company Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter company description"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact.email">Email *</label>
                  <input
                    type="email"
                    id="contact.email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleInputChange}
                    placeholder="company@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact.phone">Phone</label>
                  <input
                    type="tel"
                    id="contact.phone"
                    name="contact.phone"
                    value={formData.contact.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contact.website">Website</label>
                <input
                  type="url"
                  id="contact.website"
                  name="contact.website"
                  value={formData.contact.website}
                  onChange={handleInputChange}
                  placeholder="https://www.company.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="address.street">Street Address</label>
                  <input
                    type="text"
                    id="address.street"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleInputChange}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address.city">City</label>
                  <input
                    type="text"
                    id="address.city"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="address.state">State</label>
                  <input
                    type="text"
                    id="address.state"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address.zipCode">ZIP Code</label>
                  <input
                    type="text"
                    id="address.zipCode"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleInputChange}
                    placeholder="10001"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address.country">Country</label>
                <input
                  type="text"
                  id="address.country"
                  name="address.country"
                  value={formData.address.country}
                  onChange={handleInputChange}
                  placeholder="United States"
                />
              </div>

              <div className="form-row">
                {/* <div className="form-group">
                  <label htmlFor="assignedTo">Assigned To</label>
                  <select
                    id="assignedTo"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                  >
                    <option value="">Select User</option>
                    {users.map(user => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div> */}

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={loading}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  <FaSave /> {loading ? 'Saving...' : (editingCompany ? 'Update Company' : 'Add Company')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingCompany && (
        <div className="modal-overlay" onClick={() => setViewingCompany(null)}>
          <div className="modal-content view-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Company Details</h2>
              <button className="modal-close" onClick={() => setViewingCompany(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="company-view-content">
              <div className="company-info-section">
                <div className="detail-item">
                  <label>Company Name:</label>
                  <span>{viewingCompany.name}</span>
                </div>
                <div className="detail-item">
                  <label>Description:</label>
                  <span>{viewingCompany.description || 'No description available'}</span>
                </div>
                <div className="detail-item">
                  <label>Contact Person:</label>
                  <span>{viewingCompany.contactPerson || viewingCompany.assignedTo?.name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{viewingCompany.contact?.email || viewingCompany.contactEmail || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Phone:</label>
                  <span>{viewingCompany.contact?.phone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Website:</label>
                  <span>{viewingCompany.contact?.website || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Assigned To:</label>
                  <span>{viewingCompany.assignedTo?.name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${viewingCompany.status}`}>
                    {viewingCompany.status}
                  </span>
                </div>
              </div>

              <div className="address-section">
                <h3>Address</h3>
                <div className="address-details">
                  <div className="detail-item">
                    <label>Street:</label>
                    <span>{viewingCompany.address?.street || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>City:</label>
                    <span>{viewingCompany.address?.city || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>State:</label>
                    <span>{viewingCompany.address?.state || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>ZIP Code:</label>
                    <span>{viewingCompany.address?.zipCode || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Country:</label>
                    <span>{viewingCompany.address?.country || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
                <button className="btn-primary" onClick={openLedgerForCompany}>
                  Add Ledger Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        defaultDescription={viewingCompany ? `Company ${viewingCompany.name} adjustment` : ''}
      />
    </div>
  );
};

export default Companies;
